import React, { useState, useMemo, useEffect } from 'react';
import { Search, Plus, } from 'lucide-react';

import { useSaveCommunityMutationSTA as useSaveCommunityMutation } from '@frontend/app/containers/Communities/AddOrEditCommunity/hooks/useSaveCommunityMutationSTA';
import { useUploadContent } from '@frontend/app/hooks';
import { PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@/shadcn/components/ui/button';
import { Dialog, DialogContent } from '@/shadcn/components/ui/dialog';
import { Input } from '@/shadcn/components/ui/input';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Card, CardContent, CardFooter, CardHeader } from '@/shadcn/components/ui/card';
import { Separator } from '@/shadcn/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/shadcn/components/ui/avatar';
import { Textarea } from '@/shadcn/components/ui/textarea';

interface GroupOption {
  value: string | number;
  displayName: string;
  image: string | null;
}

interface SelectedGroup {
  fieldId: string | number;
  fieldValue: string;
}

interface GroupSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedGroups: SelectedGroup[]) => void;
  availableGroups: GroupOption[];
  initialSelectedGroups?: SelectedGroup[];
  onGroupCreated?: (newGroup: GroupOption) => void;
}

// Generate initials from group name for avatar placeholder
const getGroupInitials = (groupName: string): string => {
  return groupName
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

export const GroupSelector: React.FC<GroupSelectorProps> = ({
  isOpen,
  onClose,
  onSave,
  availableGroups,
  initialSelectedGroups = [],
  onGroupCreated,
}) => {
  const [search, setSearch] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>(initialSelectedGroups);
  const [isNewGroupModalOpen, setIsNewGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupImage, setNewGroupImage] = useState<File | null>(null);
  const [newGroupImagePreviewUrl, setNewGroupImagePreviewUrl] = useState('');

  // Upload hook for community images
  const { upload: uploadImage, isUploading: isUploadingImage } = useUploadContent({
    serviceName: 'community',
    isTemp: true,
  });

  // SaveCommunityMutation hook
  const [saveCommunity, { loading: isCreatingGroup }] = useSaveCommunityMutation({
    onCompleted: (data) => {
      const newGroup: GroupOption = {
        value: data.community.id,
        displayName: data.community.title,
        image: data.community.splashImageUrl,
      };

      // Automatically select the newly created group
      const newSelectedGroup: SelectedGroup = {
        fieldId: newGroup.value,
        fieldValue: newGroup.displayName,
      };
      setSelectedGroups((prev) => [...prev, newSelectedGroup]);

      // Notify parent component of the new group
      if (onGroupCreated) {
        onGroupCreated(newGroup);
      }

      // Reset form state
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupImage(null);
      setNewGroupImagePreviewUrl('');
      setIsNewGroupModalOpen(false);
    },
    onError: (error) => {
      console.error('Failed to create group:', error);
      // You might want to show a toast notification here
    },
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedGroups(initialSelectedGroups);
      setSearch('');
    }
  }, [isOpen, initialSelectedGroups]);

  // Use available groups directly
  const allGroups = useMemo(() => availableGroups, [availableGroups]);

  // Filter groups based on search
  const filteredGroups = useMemo(() => {
    if (!search) return allGroups;
    return allGroups.filter((group) =>
      group.displayName.toLowerCase().includes(search.toLowerCase())
    );
  }, [allGroups, search]);

  // Check if a group is selected
  const isGroupSelected = (group: GroupOption): boolean => {
    const gid = String(group.value);
    return selectedGroups.some((selected) => String(selected.fieldId) === gid);
  };

  // Toggle group selection
  const toggleGroup = (group: GroupOption) => {
    const isCurrentlySelected = isGroupSelected(group);

    if (isCurrentlySelected) {
      // Remove group
      setSelectedGroups((prev) =>
        prev.filter((selected) => String(selected.fieldId) !== String(group.value))
      );
    } else {
      // Add group
      setSelectedGroups((prev) => [
        ...prev,
        { fieldId: group.value, fieldValue: group.displayName },
      ]);
    }
  };

  // Handle save
  const handleSave = () => {
    onSave(selectedGroups);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  // Check if selection has changed
  const hasSelectionChanged = useMemo(() => {
    if (selectedGroups.length !== initialSelectedGroups.length) return true;
    const initialIds = new Set(initialSelectedGroups.map((g) => String(g.fieldId)));
    return selectedGroups.some((g) => !initialIds.has(String(g.fieldId)));
  }, [selectedGroups, initialSelectedGroups]);

  const handleCreateNewGroupFromModal = async () => {
    if (!newGroupName.trim()) {
      return;
    }

    try {
      let imageUrl: string | undefined;

      // Upload image if one is selected
      if (newGroupImage) {
        try {
          imageUrl = await uploadImage(newGroupImage, 'image', {
            imageCompressionEnabled: true,
          });
        } catch (uploadErr) {
          console.error('Failed to upload image:', uploadErr);
          // Continue with group creation even if image upload fails
        }
      }

      await saveCommunity({
        variables: {
          community: {
            title: newGroupName.trim(),
            description: newGroupDescription.trim() || newGroupName.trim(),
            splashImageUrl: imageUrl || undefined,
          },
        },
      });
    } catch (error) {
      // Error is handled in the onError callback of the mutation
      console.error('Error creating group:', error);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      if (file.size <= 2 * 1024 * 1024) { // 2MB limit
        setNewGroupImage(file);
        // Create preview URL for display
        const previewUrl = URL.createObjectURL(file);
        setNewGroupImagePreviewUrl(previewUrl);
      } else {
        alert('Image must be smaller than 2MB');
      }
    } else {
      alert('Please select a PNG or JPG image');
    }
  };

  return (
    <>
      <Dialog open={isOpen && !isNewGroupModalOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[520px] p-0 rounded-2xl border-0 shadow-none bg-grey-0">
          <Card className="flex flex-col w-[520px] bg-grey-0 rounded-2xl border-0 shadow-none">
            <CardHeader className="flex items-center justify-between gap-2.5 p-4 flex-row space-y-0">
              <div className="flex items-start gap-2 flex-1">
                <h2 className="font-medium-200-h4 font-[number:var(--medium-200-h4-font-weight)] text-grey-6 text-[length:var(--medium-200-h4-font-size)] tracking-[var(--medium-200-h4-letter-spacing)] leading-[var(--medium-200-h4-line-height)] [font-style:var(--medium-200-h4-font-style)]">
                  Add to Groups
                </h2>
              </div>
            </CardHeader>

            <Separator className="bg-grey-2" />

            <CardContent className="flex flex-col items-start gap-4 p-4">
              <p className="font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-grey-6 text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)]">
                Add members to the selected groups 1
              </p>

              <div className="flex items-start gap-2 w-full">
                <div className="flex flex-col items-start flex-1">
                  <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-grey-6" />
                    <Input
                      placeholder="Search Groups..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 bg-grey-0 border-grey-2 font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)] placeholder:text-grey-4"
                    />
                  </div>
                </div>
              </div>

              {/* No results message */}
              {search && filteredGroups.length === 0 && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg w-full">
                <div className="text-sm text-gray-700 text-center">
                  No groups found with this name.
                </div>
              </div>
            )}

              {/* Empty state when no search and no groups */}
              {filteredGroups.length === 0 && !search && (
              <div className="text-center py-8 text-grey-4 w-full">
                <div className="text-2xl mb-2">👥</div>
                <p className="text-sm">No groups found.</p>
              </div>
            )}

              {/* Groups List */}
              <div className="w-full space-y-2 max-h-60 overflow-y-auto">
                {filteredGroups.map((group) => (
                  <div key={`${group.value}-${group.displayName}`} className="flex items-center gap-4 w-full">
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        id={`group-${group.value}`}
                        checked={isGroupSelected(group)}
                        disabled={false}
                        onCheckedChange={() => toggleGroup(group)}
                        className="w-[18px] h-[18px] border-grey-3 data-[state=checked]:bg-grey-5 data-[state=checked]:border-grey-5"
                      />
                      <div className="flex items-center gap-3 flex-1">
                        {group.image ? (
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={group.image} />
                          </Avatar>
                        ) : (
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-blue-100 text-blue-600 text-xs font-medium">
                              {getGroupInitials(group.displayName)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <span className="font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-grey-6 text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)]">
                          {group.displayName}
                        </span>
                      </div>
                    </div>
                  </div>
              ))}
              </div>

              {/* Selection count */}
              <div className="text-xs text-grey-4 text-center w-full">
                {selectedGroups.length} groups selected
              </div>
            </CardContent>

            <Separator className="bg-grey-2" />

            <CardFooter className="flex items-center justify-between gap-2 p-4 bg-transparent">
              <Button
                variant="outline"
                onClick={() => setIsNewGroupModalOpen(true)}
                className="h-auto bg-grey-0 border-grey-2 text-grey-6 font-medium-100 font-[number:var(--medium-100-font-weight)] text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)] hover:bg-grey-0 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                New Group
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="h-auto bg-grey-0 border-grey-2 text-grey-6 font-medium-100 font-[number:var(--medium-100-font-weight)] text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)] hover:bg-grey-0"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasSelectionChanged}
                  className="h-auto bg-grey-6 text-grey-0 font-medium-100 font-[number:var(--medium-100-font-weight)] text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)] hover:bg-grey-5"
                >
                  Select
                </Button>
              </div>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>

      {/* New Group Creation Modal - Full Featured Modal */}
      <Dialog
        open={isNewGroupModalOpen}
        onOpenChange={() => {
          setIsNewGroupModalOpen(false);
          setNewGroupName('');
          setNewGroupDescription('');
          setNewGroupImage(null);
          setNewGroupImagePreviewUrl('');
          if (newGroupImagePreviewUrl) {
            URL.revokeObjectURL(newGroupImagePreviewUrl);
          }
        }}
      >
        <DialogContent className="max-w-[520px] p-0 rounded-2xl border-0 shadow-none bg-grey-0">
          <Card className="flex flex-col w-[520px] bg-grey-0 rounded-2xl border-0 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between p-4 space-y-0">
              <h2 className="m-0 font-medium-200-h4 font-[number:var(--medium-200-h4-font-weight)] text-grey-6 text-[length:var(--medium-200-h4-font-size)] tracking-[var(--medium-200-h4-letter-spacing)] leading-[var(--medium-200-h4-line-height)] [font-style:var(--medium-200-h4-font-style)]">
                Create New Group
              </h2>
            </CardHeader>

            <Separator className="bg-grey-2" />

            <CardContent className="flex flex-col items-start gap-6 p-4">
              <div className="grid grid-cols-2 gap-6 w-full">
                {/* Left Column - Name and Description */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="font-medium-100 font-[number:var(--medium-100-font-weight)] text-grey-6 text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)]">
                      Name
                    </label>
                    <Input
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Name your group"
                      className="bg-grey-0 border-grey-2 font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)] placeholder:text-grey-4"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newGroupName.trim()) {
                          handleCreateNewGroupFromModal();
                        }
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="font-medium-100 font-[number:var(--medium-100-font-weight)] text-grey-6 text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)]">
                      Description
                    </label>
                    <Textarea
                      value={newGroupDescription}
                      onChange={(e) => setNewGroupDescription(e.target.value)}
                      placeholder="What is the purpose of your group:"
                      className="bg-grey-0 border-grey-2 font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)] placeholder:text-grey-4 min-h-[100px] resize-none"
                    />
                  </div>
                </div>

                {/* Right Column - Image Upload */}
                <div className="flex flex-col gap-2">
                  <label className="font-medium-100 font-[number:var(--medium-100-font-weight)] text-grey-6 text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)]">
                    Image (optional)
                  </label>
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-grey-3 rounded-lg p-6 h-[140px] bg-grey-1">
                    {newGroupImagePreviewUrl ? (
                      <div className="flex flex-col items-center gap-2">
                        <img src={newGroupImagePreviewUrl} alt="Group preview" className="w-16 h-16 object-cover rounded" />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setNewGroupImage(null);
                            setNewGroupImagePreviewUrl('');
                            if (newGroupImagePreviewUrl) {
                              URL.revokeObjectURL(newGroupImagePreviewUrl);
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          className="flex items-center gap-2"
                          onClick={() => document.getElementById('group-image-upload')?.click()}
                        >
                          <PlusIcon className="w-4 h-4" />
                          Upload
                        </Button>
                        <p className="text-xs text-grey-4">png, jpg, Max 2 Mb</p>
                      </div>
                    )}
                    <input
                      id="group-image-upload"
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>

              {/* Note */}
              <p className="text-xs text-grey-5 w-full">
                <strong>Note:</strong> You can configure the custom fields for this group in group settings
              </p>
            </CardContent>

            <Separator className="bg-grey-2" />

            <CardFooter className="flex items-center justify-end gap-2 p-4 bg-transparent">
              <Button
                variant="outline"
                onClick={() => {
                  setIsNewGroupModalOpen(false);
                  setNewGroupName('');
                  setNewGroupDescription('');
                  setNewGroupImage(null);
                  setNewGroupImagePreviewUrl('');
                }}
                disabled={isCreatingGroup}
                className="h-auto bg-grey-0 border-grey-2 text-grey-6 font-medium-100 font-[number:var(--medium-100-font-weight)] text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)] hover:bg-grey-0"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateNewGroupFromModal}
                disabled={!newGroupName.trim() || isCreatingGroup || isUploadingImage}
                className="h-auto bg-grey-6 text-grey-0 font-medium-100 font-[number:var(--medium-100-font-weight)] text-[length:var(--medium-100-font-size)] tracking-[var(--medium-100-letter-spacing)] leading-[var(--medium-100-line-height)] [font-style:var(--medium-100-font-style)] hover:bg-grey-5"
              >
                {isCreatingGroup || isUploadingImage ? 'Creating...' : 'Create Group'}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};
